import React, { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import {
  Formik,
  Form as FormikForm,
} from 'formik';
import usePaymentMethods from 'components/Common/Billing/Hooks/usePaymentMethods';
import {
  Form as BaseForm,
  TextField,
  SubmitButton,
  CancelPopupButton,
  SelectField,
} from '../../ui/FormElement';

import NewManagerForm from './CreateForm/NewManager';
import ManagerFromMemberForm from './CreateForm/ManagerFromMember';

import routes from '../../app/routes';

export default function ManagerCreateForm({
  closeForm,
  onCreate,
  enterpriseId,
  teamId,
  roles,
  createMethod,
}) {
  const validateHash = {
    email: Yup.string().required('Email is required'),
    password: Yup.string().when(['role'], {
      is: (role) => {
        const itemRole = roles.find((item, index, obj) => item.value == role);
        return (
          itemRole != undefined
          && itemRole.automatic_onboarding
          && !itemRole.billing_manager
        );
      },
      then: Yup.string().required('Password is required'),
    }),
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    enterprises: Yup.number().typeError('Please, choose Enterprise'),
    teams: Yup.array().when(['role'], {
      is: (role) => {
        const itemRole = roles.find((item, index, obj) => item.value == role);
        return (
          itemRole != undefined
          && !itemRole.label.includes('Administrator')
          && !itemRole.billing_manager
        );
      },
      then: Yup.array().min(1, 'Please, choose teams'),
    }),
    role: Yup.number().typeError('Please, choose permission level'),
  };

  if (createMethod === 'member') {
    validateHash.member = Yup.number().typeError(
      'Please, choose member',
    );
  } else {
    validateHash.member_team = Yup.number().typeError(
      "Please, choose team for manager's member",
    );
  }

  const FormSchema = Yup.object().shape(validateHash);

  const [loader, setLoader] = useState(false);
  const [isAdministratorForm, setIsAdministratorForm] = useState(false);
  const [isBillingManagerForm, setIsBillingManagerForm] = useState(false);
  const [isAutomaticOnboarderForm, setIsAutomaticOnboarderForm] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    enterprises: null,
    teams: [],
    role: null,
    member_team: null,
    member: null,
    paymentMethod: null,
  });

  const [enterprises, setEnterprises] = useState();
  const [enterprisesRawData, setEnterprisesRawData] = useState();
  const [teams, setTeams] = useState();
  const [members, setMembers] = useState(null);
  const [selectedMemberTeam, setSelectedMemberTeam] = useState(null);

  const selectEnterprise = useRef(null);
  const selectTeam = useRef(null);
  const selectMemberTeam = useRef(null);
  const selectMemberRef = useRef(null);
  const selectPaymentMethod = useRef(null);

  const filteredMembers = members === null ? null : members.filter((item) => selectedMemberTeam === null || parseInt(item.team_id, 10) === selectedMemberTeam);

  const [paymentMethods, setPaymentMethods] = useState(null);

  // if (enterpriseId !== undefined) {
  //  loadPaymentMethods(enterpriseId);
  // }

  const loadEnterprises = () => {
    axios.get(routes.api.enterprises.list).then((resp) => {
      setEnterprisesRawData(resp.data.data);

      const values = resp.data.data.map((item) => ({
        value: item.id,
        label: item.attributes.name,
      }));

      setEnterprises(values);

      if (enterpriseId != null) {
        const selectedValue = values.find(
          (element) => parseInt(element.value) == parseInt(enterpriseId),
        );
        selectEnterprise.current.select.setValue(selectedValue);
      }
    });
  };

  const loadTeams = (value) => {
    const enterprise = enterprisesRawData.find(
      (element) => parseInt(element.id) == parseInt(value),
    );

    const values = enterprise.attributes.teams.map((item) => ({
      value: item.id,
      label: item.title,
    }));

    setTeams(values);

    if (teamId != null) {
      const selectedValue = values.find(
        (element) => parseInt(element.value) == parseInt(teamId),
      );
      selectTeam.current.select.setValue([selectedValue]);
      selectMemberTeam.current.select.setValue(selectedValue);
    }
  };

  const loadMembers = (enterpriseId) => {
    axios.get(`${routes.api.enterprises.members(enterpriseId)}?q[manager_id_null]=1`).then((resp) => {
      const values = resp.data.data.map((item) => ({
        value: item.id,
        label: item.attributes.full_name,
        team_id: item.attributes.team_id,
        first_name: item.attributes.first_name,
        last_name: item.attributes.last_name,
        email: item.attributes.email,
      }));

      setMembers(values);
    });
  };

  const loadPaymentMethods = (id) => {
    axios
      .get(routes.api.enterprises.payment_methods(id))
      .then((response) => {
        setPaymentMethods(response.data.data);
      });
  };

  const onEnterpriseChange = (id) => {
    if (selectTeam.current) {
      selectTeam.current.select.clearValue();
    }

    selectMemberTeam.current.select.clearValue();
    if (selectMemberRef.current) {
      selectMemberRef.current.select.clearValue();
    }

    if (selectPaymentMethod.current) {
      selectPaymentMethod.current.select.clearValue();
    }

    setPaymentMethods(null);
    if (id !== undefined) {
      loadTeams(id);
      if (createMethod === 'member') {
        loadMembers(id);
      }
      loadPaymentMethods(id);
    } else {
      setTeams([]);
      if (createMethod === 'member') {
        setMembers([]);
      }
    }
  };

  const onMemberTeamChange = (id) => {
    if (selectMemberRef.current) {
      selectMemberRef.current.select.clearValue();
    }
    setSelectedMemberTeam(id);
  };

  const enableFields = (option) => {
    if (option.label.includes('Admin') || option.billing_manager) {
      setIsAdministratorForm(true);
      if (selectTeam.current != null) {
        selectTeam.current.select.setValue([]);
      }
    } else {
      setIsAdministratorForm(false);
    }
    if (option.automatic_onboarding && !option.billing_manager) {
      setIsAutomaticOnboarderForm(true);
    } else {
      setIsAutomaticOnboarderForm(false);
    }
    setIsBillingManagerForm(option.billing_manager);
  };

  const prepareFormDataForSend = (values) => {
    const formData = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      password: values.password,
      enterprise_id: values.enterprises,
      team_ids: values.teams,
      user_role_id: values.role,
      member_team: values.member_team,
      member: values.member,
      payment_information_id: values.paymentMethod,
    };
    return formData;
  };

  const formSendSuccess = () => {
    closeForm();
    swal('Manager created.');

    onCreate();
  };

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    setSubmitting(false);
    setLoader(true);
    axios
      .post(routes.api.users.registration, {
        user: { ...prepareFormDataForSend(values) },
        manager: 1,
        admin_create: 1,
      })
      .then((resp) => {
        setLoader(false);

        formSendSuccess();
      })
      .catch((error, data) => {
        setLoader(false);

        error.response.data.errors.forEach((element) => {
          switch (element.source.pointer) {
            case '/data/attributes/password':
              setErrors({ password: element.detail });
              break;
            case '/data/attributes/email':
              setErrors({ email: element.detail });
              break;
            default:
              setErrors({ email: element.detail });
          }
        });
      });
  };

  useEffect(() => {
    loadEnterprises();
  }, []);

  return (
    <Formik
      enableReinitialize
      validationSchema={FormSchema}
      initialValues={formData}
      onSubmit={onSubmit}
    >
      {({ setFieldValue }) => (
        <FormikForm className="needs-validation" noValidate="">
          {createMethod === 'member' ? (
            <ManagerFromMemberForm
              roles={roles}
              enableFields={enableFields}
              isAutomaticOnboarderForm={isAutomaticOnboarderForm}
              selectEnterprise={selectEnterprise}
              enterprises={enterprises}
              onEnterpriseChange={onEnterpriseChange}
              isAdministratorForm={isAdministratorForm}
              selectTeam={selectTeam}
              teams={teams}
              selectMemberTeam={selectMemberTeam}
              onMemberTeamChange={onMemberTeamChange}
              filteredMembers={filteredMembers}
              setFieldValue={setFieldValue}
              selectMemberRef={selectMemberRef}
              paymentMethods={paymentMethods}
              isBillingManagerForm={isBillingManagerForm}
              selectPaymentMethod={selectPaymentMethod}
            />
          ) : (
            <NewManagerForm
              roles={roles}
              enableFields={enableFields}
              isAutomaticOnboarderForm={isAutomaticOnboarderForm}
              selectEnterprise={selectEnterprise}
              enterprises={enterprises}
              onEnterpriseChange={onEnterpriseChange}
              isAdministratorForm={isAdministratorForm}
              selectTeam={selectTeam}
              teams={teams}
              selectMemberTeam={selectMemberTeam}
              onMemberTeamChange={onMemberTeamChange}
              filteredMembers={filteredMembers}
              setFieldValue={setFieldValue}
              paymentMethods={paymentMethods}
              isBillingManagerForm={isBillingManagerForm}
              selectPaymentMethod={selectPaymentMethod}
            />
          )}

          <div className="text-center mt50 mb30">
            <CancelPopupButton
              className="btn btn-primary btn-outline mr5 ml5 width-120"
              title="Cancel"
              onClick={closeForm}
            />
            <SubmitButton
              title={loader ? 'Please, wait ...' : 'Create'}
              className="btn width-120 btn-primary submit-button mr5 ml5"
            />
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}
